/**
 * The `POST /billing/confirm` vocabulary.
 *
 * Called on the post-Stripe redirect. It replaced
 * `POST /stripe/successful-payment-intent` and keeps that endpoint's three
 * conversion-pixel fields
 * verbatim. `/post-purchase` decides which Purchase pixels to fire from them,
 * and the rules behind each were expensive to get right (a trialing
 * subscription reports the *eventual* price, so `amount_usd` is null until money
 * actually moves; under-counting is the safe direction).
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md
 */
import { Schema } from 'effect';
import { BillingStatus } from './billing-status';

export const ConfirmResponse = Schema.Struct({
  /**
   * The same shape `GET /billing/status` returns, resolved **after** the eager
   * sync — so a client that lands before the webhook does still sees the truth.
   */
  status: BillingStatus,
  /**
   * Revenue **at this moment**, not the sticker price: null while trialing, null
   * when the subscription is not yet active, null when the line item has no
   * amount. `/post-purchase` treats null as "skip every Purchase pixel".
   *
   * Strictly positive or null — zero is unrepresentable, so "no money moved" can
   * only ever arrive as null. A `$0` Purchase pixel is a counted conversion that
   * never happened, and the decoder is the cheapest place to refuse one.
   */
  amount_usd: Schema.NullOr(
    Schema.Finite.pipe(Schema.check(Schema.isGreaterThan(0))),
  ),
  currency: Schema.NullOr(Schema.String),
  /**
   * Sourced from Stripe's own status, never from a UI trial flag — the flag
   * gates whether checkout *offers* a trial; this says whether one started.
   */
  trial_started: Schema.Boolean,
  purchase_event_id: Schema.optional(Schema.NullOr(Schema.String)),
});
export type ConfirmResponse = typeof ConfirmResponse.Type;
